import { lazy, type ReactNode } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthProvider.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { Estado } from './components/ui.js';
import { DataProvider } from './data/DataProvider.js';
import { LoginPage } from './features/auth/LoginPage.js';
import { EntrenarPage } from './features/entrenar/EntrenarPage.js';
import { ReadinessPage } from './features/entrenar/ReadinessPage.js';
import { SesionActivaPage } from './features/entrenar/SesionActivaPage.js';
import { SesionCierrePage } from './features/entrenar/SesionCierrePage.js';
import { SesionPreviewPage } from './features/entrenar/SesionPreviewPage.js';
import { InicioPage } from './features/inicio/InicioPage.js';
import { AppShell } from './layouts/AppShell.js';
import './styles/global.css';

/*
 * División de código por rutas (PERF-002).
 *
 * Las 24 páginas se importaban estáticamente aquí, así que el primer arranque
 * descargaba y parseaba la aplicación ENTERA —incluidas la importación, el
 * borrado de cuenta y el coste de IA, que se abren una vez cada varios meses—
 * antes de pintar nada. En un Android de gama media con arranque en frío eso se
 * nota, y el propio build lo avisaba en cada compilación.
 *
 * LA REGLA: es inmediato lo que se pinta en el primer render, MÁS todo lo que
 * hay que poder abrir con el móvil ya sin cobertura. Diferido, el resto.
 *
 *  - `AppShell`: es el marco. Diferirlo movería la navegación entera.
 *  - `LoginPage`: lo primero que ve una instalación nueva. Sin sesión no hay
 *    nada más que cargar.
 *  - `InicioPage`: es la ruta índice, o sea lo que aparece al tocar el icono.
 *    Diferirla añadiría una petición ANTES del primer pintado, que es
 *    exactamente lo que este hallazgo intenta quitar.
 *  - Las cinco del entrenamiento —`EntrenarPage`, `SesionPreviewPage`,
 *    `ReadinessPage`, `SesionActivaPage` y `SesionCierrePage`—: el camino
 *    entero, de abrir la sesión a cerrarla.
 *
 * POR QUÉ EL ENTRENAMIENTO SE QUEDA ENTERO, y no es una precaución:
 *
 * Se probó a diferirlo. Ahorraba 94 kB (33 kB gzip) por arranque en frío y
 * `npm run verify` pasaba en verde. Lo que lo tumbó fue `e2e-5-offline`: se
 * corta la red con la aplicación ya abierta y se entrena entera. Con las rutas
 * diferidas, la navegación interna a `/entrenar/sesion/:id` **se queda en el
 * marco vacío**, porque el trozo no está descargado y no hay red para pedirlo.
 * Dos de los treinta y cuatro E2E en rojo, y no por el arnés: por la
 * aplicación.
 *
 * En producción el service worker precachea todos los `.js` del build
 * —`globPatterns` en `vite.config.ts`—, así que en teoría estarían. Pero
 * «en teoría» es justo lo que este repositorio tiene documentado como trampa:
 * un artefacto que el build produce bien y que nadie abre envejece igual. La
 * PRIMERA visita después de un despliegue, con el service worker todavía
 * instalándose, es una ventana real; y entrenar sin cobertura es el §5, no un
 * caso raro. 94 kB no valen esa ventana.
 *
 * Todo lo demás es diferido a nivel de RUTA, no de componente: un trozo por
 * pantalla se cachea bien y se entiende leyendo este fichero. `CardioPage`
 * también, porque no forma parte del registro de una sesión.
 */

const CardioPage = lazy(async () => ({
  default: (await import('./features/entrenar/CardioPage.js')).CardioPage,
}));
const ComidaPage = lazy(async () => ({
  default: (await import('./features/nutricion/ComidaPage.js')).ComidaPage,
}));
const NutricionPage = lazy(async () => ({
  default: (await import('./features/nutricion/NutricionPage.js')).NutricionPage,
}));
const CompraPage = lazy(async () => ({
  default: (await import('./features/compra/CompraPage.js')).CompraPage,
}));
const DespensaPage = lazy(async () => ({
  default: (await import('./features/compra/DespensaPage.js')).DespensaPage,
}));
const PlanPage = lazy(async () => ({
  default: (await import('./features/plan/PlanPage.js')).PlanPage,
}));
const ProgresoPage = lazy(async () => ({
  default: (await import('./features/progreso/ProgresoPage.js')).ProgresoPage,
}));
const GimnasioPage = lazy(async () => ({
  default: (await import('./features/mas/GimnasioPage.js')).GimnasioPage,
}));
const MasPage = lazy(async () => ({
  default: (await import('./features/mas/MasPage.js')).MasPage,
}));
const PerfilPage = lazy(async () => ({
  default: (await import('./features/mas/PerfilPage.js')).PerfilPage,
}));
const PrioridadesPage = lazy(async () => ({
  default: (await import('./features/mas/PrioridadesPage.js')).PrioridadesPage,
}));
const ObjetivoPage = lazy(async () => ({
  default: (await import('./features/mas/ObjetivoPage.js')).ObjetivoPage,
}));
const EvidenciaPage = lazy(async () => ({
  default: (await import('./features/mas/EvidenciaPage.js')).EvidenciaPage,
}));
const ComidasPage = lazy(async () => ({
  default: (await import('./features/mas/ComidasPage.js')).ComidasPage,
}));
const AvisosPage = lazy(async () => ({
  default: (await import('./features/mas/AvisosPage.js')).AvisosPage,
}));
const IACostePage = lazy(async () => ({
  default: (await import('./features/mas/IACostePage.js')).IACostePage,
}));
const PropuestasPage = lazy(async () => ({
  default: (await import('./features/mas/PropuestasPage.js')).PropuestasPage,
}));
const EliminarCuentaPage = lazy(async () => ({
  default: (await import('./features/mas/EliminarCuentaPage.js')).EliminarCuentaPage,
}));
const ExportarPage = lazy(async () => ({
  default: (await import('./features/mas/ExportarPage.js')).ExportarPage,
}));
const ImportarPage = lazy(async () => ({
  default: (await import('./features/mas/ImportarPage.js')).ImportarPage,
}));
const SincronizacionPage = lazy(async () => ({
  default: (await import('./features/mas/SincronizacionPage.js')).SincronizacionPage,
}));
const TestInicialPage = lazy(async () => ({
  default: (await import('./features/bienvenida/TestInicialPage.js')).TestInicialPage,
}));

function RutasPrivadas(): ReactNode {
  return (
    <DataProvider>
      {/*
        El `Suspense` de estas rutas vive en `AppShell`, alrededor de su
        `Outlet`, junto a la frontera de error que ya había. Ahí el esqueleto
        sustituye solo el contenido y las barras de navegación no se mueven; y
        el fallback es `Estado tipo="cargando"`, el mismo que usa toda la
        aplicación mientras lee de Dexie —ocupa sitio, no deja la pantalla en
        blanco y lo anuncia por `role="status"`—, así que no hace falta ni un
        componente nuevo ni una dependencia nueva.
      */}
      <Routes>
        <Route element={<AppShell />}>
          <Route index element={<InicioPage />} />
          <Route path="bienvenida" element={<TestInicialPage />} />
          <Route path="plan" element={<PlanPage />} />
          <Route path="plan/nutricion" element={<NutricionPage />} />
          <Route path="plan/nutricion/:mealId" element={<ComidaPage />} />
          <Route path="plan/compra" element={<CompraPage />} />
          <Route path="plan/despensa" element={<DespensaPage />} />
          <Route path="entrenar" element={<EntrenarPage />} />
          <Route path="entrenar/cardio" element={<CardioPage />} />
          <Route path="entrenar/sesion/:sessionId" element={<SesionPreviewPage />} />
          <Route path="entrenar/sesion/:sessionId/readiness" element={<ReadinessPage />} />
          <Route path="entrenar/sesion/:sessionId/activa" element={<SesionActivaPage />} />
          <Route path="entrenar/sesion/:sessionId/cierre" element={<SesionCierrePage />} />
          <Route path="progreso" element={<ProgresoPage />} />
          <Route path="mas" element={<MasPage />}>
            <Route path="perfil" element={<PerfilPage />} />
            <Route path="gimnasio" element={<GimnasioPage />} />
            <Route path="prioridades" element={<PrioridadesPage />} />
            <Route path="comidas" element={<ComidasPage />} />
            <Route path="objetivo" element={<ObjetivoPage />} />
            <Route path="evidencia" element={<EvidenciaPage />} />
            <Route path="propuestas" element={<PropuestasPage />} />
            <Route path="ia" element={<IACostePage />} />
            <Route path="avisos" element={<AvisosPage />} />
            <Route path="sincronizacion" element={<SincronizacionPage />} />
            <Route path="exportar" element={<ExportarPage />} />
            <Route path="importar" element={<ImportarPage />} />
            <Route path="eliminar-cuenta" element={<EliminarCuentaPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </DataProvider>
  );
}

function Contenido(): ReactNode {
  const { status } = useAuth();

  if (status === 'loading') {
    return <Estado tipo="cargando" titulo="Comprobando sesión" />;
  }
  if (status === 'signed_in') {
    return <RutasPrivadas />;
  }
  return <LoginPage />;
}

export function App(): ReactNode {
  return (
    <Router>
      <AuthProvider>
        {/* Frontera exterior: cubre el arranque, el login y todo lo que quede
            fuera del AppShell. Sin ella, un fallo aqui deja la pantalla vacia
            y sin un solo mensaje que leer. */}
        <ErrorBoundary ambito="aplicación">
          <Contenido />
        </ErrorBoundary>
      </AuthProvider>
    </Router>
  );
}
